import { callable } from '../callable';

// ─── DTOs ─────────────────────────────────────────────────────────────────────

export interface ScholarshipDto {
  id: string;
  name: string;
  description: string;
  coveragePercent: number;
  applicationDeadline: string | null;
  isActive: boolean;
}

export interface ScholarshipApplicationDto {
  id: string;
  status: 'draft' | 'submitted' | 'reviewing' | 'approved' | 'rejected';
  submittedAt: string | null;
  approvedCoveragePercent: number | null;
  reviewNotes: string | null;
  reviewedAt: string | null;
}

export interface StudentScholarshipStatusDto {
  scholarship: ScholarshipDto | null;
  application: ScholarshipApplicationDto | null;
}

export interface ApplyForScholarshipRequest {
  tenantId: string;
  scholarshipId: string;
  motivationStatement: string;
  financialStatement: string;
  employmentStatus: 'employed' | 'unemployed' | 'part-time';
  economicDependents: number;
  hadScholarshipBefore: boolean;
  previousScholarshipYear?: string;
  documentUrls?: string[];
  submit?: boolean;
  personId?: string;
}

export interface ScholarshipApplicationSnapshotDto {
  id: string;
  scholarshipId: string;
  termId: string;
  studentId: string;
  status: string;
  motivationStatement: string;
  financialStatement: string;
  employmentStatus: string;
  economicDependents: number;
  hadScholarshipBefore: boolean;
  previousScholarshipYear: string | null;
  approvedCoveragePercent: number | null;
  reviewNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  submittedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ScholarshipSnapshotDto {
  id: string;
  tenantId: string;
  termId: string;
  name: string;
  description: string;
  coveragePercent: number;
  maxApplicants: number;
  eligibleProgramIds: string[];
  applicationDeadline: string | null;
  reviewerIds: string[];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** How an approval landed on the student's plan (returned by reviewApplication). */
export type ApplyScholarshipDiscountResultDto =
  | { applied: true; planId: string; tuitionDiscountAmount: number; newTotalAmount: number }
  | { applied: false; reason: 'no_plan' | 'conflict' | 'inactive_plan' };

export interface ProgramScholarshipAwardDto {
  id: string;
  tenantId: string;
  studentId: string;
  programId: string;
  coveragePercent: number;
  waiveEnrollment: boolean;
  name: string;
  notes: string | null;
  status: 'active' | 'revoked';
  grantedBy: string;
  grantedAt: string;
  revokedBy: string | null;
  revokedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AssignProgramScholarshipResult {
  id: string;
  plansApplied: number;
  plansInConflict: number;
}

// ─── Scholarship API ──────────────────────────────────────────────────────────

export const scholarshipApi = {
  getMyScholarshipStatus: async (params: {
    tenantId: string;
    termId: string;
    personId?: string;
  }): Promise<StudentScholarshipStatusDto> => {
    const fn = callable<typeof params, StudentScholarshipStatusDto>('getMyScholarshipStatus');
    const result = await fn(params);
    return result.data;
  },

  applyForScholarship: async (
    data: ApplyForScholarshipRequest,
  ): Promise<{ id: string; status: string }> => {
    const fn = callable<ApplyForScholarshipRequest, { id: string; status: string }>(
      'applyForScholarship',
    );
    const result = await fn(data);
    return result.data;
  },

  // Admin
  listScholarshipApplications: async (params: {
    tenantId: string;
    scholarshipId: string;
  }): Promise<ScholarshipApplicationSnapshotDto[]> => {
    const fn = callable<typeof params, ScholarshipApplicationSnapshotDto[]>(
      'listScholarshipApplications',
    );
    const result = await fn(params);
    return result.data;
  },

  reviewApplication: async (params: {
    tenantId: string;
    applicationId: string;
    decision: 'approve' | 'reject';
    approvedCoveragePercent?: number;
    notes?: string;
  }): Promise<{
    id: string;
    status: string;
    discount: ApplyScholarshipDiscountResultDto | null;
  }> => {
    const fn = callable<
      typeof params,
      { id: string; status: string; discount: ApplyScholarshipDiscountResultDto | null }
    >('reviewScholarshipApplication');
    const result = await fn(params);
    return result.data;
  },

  createScholarship: async (params: {
    tenantId: string;
    termId: string;
    name: string;
    description: string;
    coveragePercent: number;
    maxApplicants?: number;
    eligibleProgramIds?: string[];
    applicationDeadline?: string | null;
    reviewerIds?: string[];
  }): Promise<{ id: string }> => {
    const fn = callable<typeof params, { id: string }>('createScholarship');
    const result = await fn(params);
    return result.data;
  },

  // ── Fixed program-award scholarships ────────────────────────────────────────
  assignProgramScholarship: async (params: {
    tenantId: string;
    studentId: string;
    programId: string;
    coveragePercent: number;
    name: string;
    notes?: string | null;
    waiveEnrollment?: boolean;
  }): Promise<AssignProgramScholarshipResult> => {
    const fn = callable<typeof params, AssignProgramScholarshipResult>('assignProgramScholarship');
    const result = await fn(params);
    return result.data;
  },

  revokeProgramScholarship: async (params: {
    tenantId: string;
    awardId: string;
  }): Promise<{ id: string; status: string }> => {
    const fn = callable<typeof params, { id: string; status: string }>('revokeProgramScholarship');
    const result = await fn(params);
    return result.data;
  },

  listProgramScholarshipAwards: async (params: {
    tenantId: string;
    studentId?: string;
    programId?: string;
  }): Promise<ProgramScholarshipAwardDto[]> => {
    const fn = callable<typeof params, ProgramScholarshipAwardDto[]>(
      'listProgramScholarshipAwards',
    );
    const result = await fn(params);
    return result.data;
  },
};
